import { useCallback, useEffect, useState } from "react";
import { PiSprayBottleFill } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioTipoAtividade } from "../../sync/repositories/agricultura";

const AJUDA = {
  titulo: "Cadastro de Tipos de Atividade",
  descricao:
    "Tipos de Atividade classificam as operações agrícolas realizadas na fazenda (ex.: Plantio, Pulverização, Colheita, Adubação). Esse cadastro é usado no Registro de Atividades para identificar qual operação foi executada em cada lançamento.",
  secoes: [
    {
      titulo: "Dados da Atividade",
      campos: [
        {
          nome: "Nome da Atividade",
          obrigatorio: true,
          explicacao: "Nome descritivo da atividade (ex.: 'Aplicação de Herbicida', 'Plantio de Soja'). Aparece nas listas de seleção do Registro de Atividades.",
        },
        {
          nome: "Tipo Principal",
          obrigatorio: true,
          explicacao: "Categoria geral da atividade (Plantio, Pulverização, Colheita, Gradagem/Preparo Solo, Adubação, Semeadura ou Outros). Define o comportamento da tela e habilita o campo de subcategoria quando aplicável.",
        },
        {
          nome: "Subcategoria da Pulverização",
          obrigatorio: true,
          explicacao: "Só aparece e só é exigida quando o Tipo Principal escolhido é 'Pulverização'. Especifica o tipo de produto aplicado (Herbicida, Inseticida, Fungicida, etc.).",
        },
        {
          nome: "Exige Máquina / Exige Funcionário / Exige Insumo",
          obrigatorio: false,
          explicacao: "Define, para este tipo de atividade, se máquina, funcionário e ao menos um insumo são obrigatórios no Registro de Atividades. Por padrão, máquina e funcionário são exigidos e insumo é opcional.",
        },
      ],
    },
  ],
  dicas: [
    "Um tipo de atividade que já tem registros de atividade vinculados não pode ser excluído — o botão 'Excluir' fica desabilitado nesse caso.",
  ],
};

export default function GestaoTiposAtividade() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [atividades, setAtividades] = useState([]);
  const [atividadesFiltradas, setAtividadesFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);

  const [filtros, setFiltros] = useState({
    busca: "",
    tipo_principal: "",
  });

  const [formData, setFormData] = useState({
    nome: "",
    tipo_principal: "",
    subcategoria_pulverizacao: "",
    exige_maquina: true,
    exige_funcionario: true,
    exige_insumo: false,
  });

  const TIPOS_PRINCIPAIS = [
    { value: "plantio", label: "Plantio" },
    { value: "pulverizacao", label: "Pulverização" },
    { value: "colheita", label: "Colheita" },
    { value: "gradagem", label: "Gradagem/Preparo Solo" },
    { value: "adubacao", label: "Adubação" },
    { value: "semeadura", label: "Semeadura" },
    { value: "outros", label: "Outros" },
  ];

  const SUBCATEGORIAS_PULV = [
    { value: "herbicida", label: "Herbicida" },
    { value: "inseticida", label: "Inseticida" },
    { value: "fungicida", label: "Fungicida" },
    { value: "acaricida", label: "Acaricida" },
    { value: "biologico", label: "Biológico" },
    { value: "nutricao_foliar", label: "Nutrição Foliar" },
    { value: "regulador", label: "Regulador de Crescimento" },
    { value: "outro_pulv", label: "Outro" },
  ];

  // Carregar tipos de atividade
  const carregarAtividades = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioTipoAtividade.listar();
      const dados = response.data.results || response.data;
      setAtividades(dados);
      setAtividadesFiltradas(dados);
    } catch (error) {
      console.error("Erro ao carregar tipos de atividade:", error);
      alert("Erro ao carregar lista de tipos de atividade");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarAtividades();
  }, [carregarAtividades]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Limpa subcategoria se mudar para algo diferente de pulverização
    if (name === "tipo_principal" && value !== "pulverizacao") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        subcategoria_pulverizacao: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const validarFormulario = () => {
    if (!formData.nome.trim()) {
      alert("Preencha o nome da atividade.");
      return false;
    }
    if (!formData.tipo_principal) {
      alert("Selecione o tipo principal da atividade.");
      return false;
    }
    if (
      formData.tipo_principal === "pulverizacao" &&
      !formData.subcategoria_pulverizacao
    ) {
      alert("Selecione a subcategoria da pulverização.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) return;

    setLoading(true);

    const payload = {
      nome: formData.nome,
      tipo_principal: formData.tipo_principal,
      subcategoria_pulverizacao:
        formData.tipo_principal === "pulverizacao"
          ? formData.subcategoria_pulverizacao
          : "",
      exige_maquina: formData.exige_maquina,
      exige_funcionario: formData.exige_funcionario,
      exige_insumo: formData.exige_insumo,
    };

    try {
      if (editando) {
        const response = await repositorioTipoAtividade.atualizar(editandoId, payload);
        if (response.status === 200) {
          alert("Tipo de atividade atualizado com sucesso!");
          handleCancel();
          carregarAtividades();
        }
      } else {
        const response = await repositorioTipoAtividade.criar(payload);
        if (response.status === 201 || response.status === 200) {
          alert("Tipo de atividade cadastrado com sucesso!");
          handleCancel();
          carregarAtividades();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (atividade) => {
    setFormData({
      nome: atividade.nome || "",
      tipo_principal: atividade.tipo_principal || "",
      subcategoria_pulverizacao: atividade.subcategoria_pulverizacao || "",
      exige_maquina: atividade.exige_maquina ?? true,
      exige_funcionario: atividade.exige_funcionario ?? true,
      exige_insumo: atividade.exige_insumo ?? false,
    });
    setEditando(true);
    setEditandoId(atividade.id);
    setMostrarFormulario(true);

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const handleNovaAtividade = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      tipo_principal: "",
      subcategoria_pulverizacao: "",
      exige_maquina: true,
      exige_funcionario: true,
      exige_insumo: false,
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (
      !window.confirm(
        "Tem certeza que deseja excluir este tipo de atividade? Esta ação não pode ser desfeita.",
      )
    ) {
      return;
    }

    try {
      await repositorioTipoAtividade.excluir(id);
      alert("Tipo de atividade excluído com sucesso!");
      carregarAtividades();

      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert(
        "Erro ao excluir tipo de atividade. Verifique se não há registros vinculados.",
      );
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...atividades];

    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter((a) =>
        a.nome?.toLowerCase().includes(buscaLower),
      );
    }

    if (filtros.tipo_principal) {
      filtrados = filtrados.filter(
        (a) => a.tipo_principal === filtros.tipo_principal,
      );
    }

    setAtividadesFiltradas(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", tipo_principal: "" });
    setAtividadesFiltradas(atividades);
  };

  const getTipoInfo = (tipoPrincipal) => {
    return (
      TIPOS_PRINCIPAIS.find((t) => t.value === tipoPrincipal) ||
      TIPOS_PRINCIPAIS[6]
    );
  };

  const getSubcategoriaLabel = (subcategoria) => {
    const sub = SUBCATEGORIAS_PULV.find((s) => s.value === subcategoria);
    return sub ? sub.label : subcategoria;
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              Gestão de Tipos de Atividade <PiSprayBottleFill size={38} />
            </h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaAtividade}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Nova Atividade
            </button>
          )}
        </div>

        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando
                  ? "Editar Tipo de Atividade"
                  : "Cadastrar Novo Tipo de Atividade"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Nome da Atividade */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Nome da Atividade *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    className="w-96 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Ex: Aplicação de Herbicida, Plantio de Soja, etc."
                    required
                  />
                  <span className="text-xs text-gray-500 mt-1">
                    Nome descritivo da atividade agrícola
                  </span>
                </div>

                {/* Linha 2: Tipo Principal */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Tipo Principal *
                  </label>
                  <select
                    name="tipo_principal"
                    value={formData.tipo_principal}
                    onChange={handleChange}
                    className="w-96 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    required
                  >
                    <option value="">Selecione o tipo principal</option>
                    {TIPOS_PRINCIPAIS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.icon} {t.label}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-gray-500 mt-1">
                    Categoria geral da atividade
                  </span>
                </div>

                {/* Subcategoria - só aparece se for pulverização */}
                {formData.tipo_principal === "pulverizacao" && (
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">
                      Subcategoria da Pulverização *
                    </label>
                    <select
                      name="subcategoria_pulverizacao"
                      value={formData.subcategoria_pulverizacao}
                      onChange={handleChange}
                      className="w-96 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    >
                      <option value="">Selecione a subcategoria</option>
                      {SUBCATEGORIAS_PULV.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.icon} {s.label}
                        </option>
                      ))}
                    </select>
                    <span className="text-xs text-gray-500 mt-1">
                      Especificação do tipo de produto aplicado
                    </span>
                  </div>
                )}

                {/* Exigências no Registro de Atividades */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">
                    Exigências no Registro de Atividades
                  </label>
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        name="exige_maquina"
                        checked={formData.exige_maquina}
                        onChange={handleChangeCheckbox}
                        className="h-4 w-4"
                      />
                      Exige Máquina
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        name="exige_funcionario"
                        checked={formData.exige_funcionario}
                        onChange={handleChangeCheckbox}
                        className="h-4 w-4"
                      />
                      Exige Funcionário
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        name="exige_insumo"
                        checked={formData.exige_insumo}
                        onChange={handleChangeCheckbox}
                        className="h-4 w-4"
                      />
                      Exige Insumo
                    </label>
                  </div>
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading
                      ? "Salvando..."
                      : editando
                        ? "Atualizar"
                        : "Cadastrar"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
                Filtros de Busca
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Buscar por Nome
                  </label>
                  <input
                    type="text"
                    placeholder="Digite o nome da atividade..."
                    value={filtros.busca}
                    onChange={(e) =>
                      setFiltros({ ...filtros, busca: e.target.value })
                    }
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Filtrar por Tipo Principal
                  </label>
                  <select
                    value={filtros.tipo_principal}
                    onChange={(e) =>
                      setFiltros({ ...filtros, tipo_principal: e.target.value })
                    }
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todos os tipos</option>
                    {TIPOS_PRINCIPAIS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.icon} {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Tipos de Atividade */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {atividadesFiltradas.length} tipo(s) de atividade
                  encontrado(s)
                </p>
              </div>

              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">
                  Carregando...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">
                          Código
                        </th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">
                          Atividade
                        </th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">
                          Tipo Principal
                        </th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">
                          Subcategoria
                        </th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">
                          Exigências
                        </th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">
                          Qtd. Registros
                        </th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {atividadesFiltradas.map((atividade) => {
                        const tipoInfo = getTipoInfo(atividade.tipo_principal);
                        return (
                          <tr
                            key={atividade.id}
                            className="border-t border-gray-200 hover:bg-gray-50 transition-colors"
                          >
                            <td className="p-3 text-sm font-mono">
                              {atividade.id}
                            </td>
                            <td className="p-3 text-sm font-medium">
                              <span className="flex items-center gap-1">
                                <svg
                                  className="w-4 h-4 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                  />
                                </svg>
                                {atividade.nome}
                              </span>
                            </td>
                            <td className="p-3">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${tipoInfo.color}`}
                              >
                                {tipoInfo.icon} {tipoInfo.label}
                              </span>
                            </td>
                            <td className="p-3 text-sm">
                              {atividade.tipo_principal === "pulverizacao" &&
                              atividade.subcategoria_pulverizacao ? (
                                <span className="flex items-center gap-1">
                                  {getSubcategoriaLabel(
                                    atividade.subcategoria_pulverizacao,
                                  )}
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="p-3 text-xs">
                              <div className="flex flex-wrap gap-1">
                                {atividade.exige_maquina && (
                                  <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Máquina</span>
                                )}
                                {atividade.exige_funcionario && (
                                  <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Funcionário</span>
                                )}
                                {atividade.exige_insumo && (
                                  <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Insumo</span>
                                )}
                                {!atividade.exige_maquina && !atividade.exige_funcionario && !atividade.exige_insumo && (
                                  <span className="text-gray-400">—</span>
                                )}
                              </div>
                            </td>
                            <td className="p-3 text-sm text-center items-center gap-1">
                              <span className="font-medium">
                                {atividade.registros_count || 0}
                              </span>
                              <span className="text-gray-500 text-xs ml-1">
                                registros
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditar(atividade)}
                                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Editar"
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={() => handleExcluir(atividade.id)}
                                  className={`px-3 py-1 rounded text-sm transition-colors ${
                                    atividade.registros_count > 0
                                      ? "bg-gray-400 cursor-not-allowed"
                                      : "bg-red-600 hover:bg-red-700 text-white"
                                  }`}
                                  title={
                                    atividade.registros_count > 0
                                      ? "Tipo com registros não pode ser excluído"
                                      : "Excluir"
                                  }
                                  disabled={atividade.registros_count > 0}
                                >
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                      {atividadesFiltradas.length === 0 && (
                        <tr>
                          <td
                            colSpan="7"
                            className="p-8 text-center text-gray-500"
                          >
                            {filtros.busca || filtros.tipo_principal
                              ? "Nenhum tipo de atividade encontrado com os filtros selecionados."
                              : "Nenhum tipo de atividade cadastrado."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
